import { useEffect, useRef } from "react";

/**
 * usePageLifecycleEvents - Shared helper hook for detecting page "resume"
 *
 * Mobile browsers (iOS Safari, mobile Edge) aggressively throttle background
 * timers and restore pages from the back/forward cache (bfcache) via the
 * `pageshow` event (`event.persisted === true`) WITHOUT reliably firing
 * `visibilitychange`. This hook registers the additional lifecycle listeners
 * (`pageshow`, `focus`, `online`) that the Page Visibility API alone misses,
 * so consumers can force an immediate refresh and re-arm their polling when
 * the user returns to a suspended/restored page.
 *
 * A cooldown guard dedups the burst of overlapping resume events (e.g.
 * pageshow + focus + visibilitychange firing together) so `onResume` runs at
 * most once per cooldown window.
 *
 * @param {function} onResume - Called when the page is resumed/restored
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Register listeners (default: true)
 * @param {number} options.cooldownMs - Dedup window in ms (default: 1000)
 *
 * @example
 * usePageLifecycleEvents(() => refresh(), {
 *   enabled: enabled && pauseWhenHidden,
 * });
 */
// Generated by GitHub Copilot
function usePageLifecycleEvents(onResume, options = {}) {
  const { enabled = true, cooldownMs = 1000 } = options;

  const savedOnResume = useRef(onResume);
  const lastResumeRef = useRef(0);

  // Keep callback reference fresh so listeners always call the latest
  // without needing to re-register on every render.
  useEffect(() => {
    savedOnResume.current = onResume;
  }, [onResume]);

  useEffect(() => {
    if (!enabled) return;

    let onlineTimeoutId = null;

    // Cooldown-guarded invocation: skip if a resume already fired within the
    // cooldown window (dedups overlapping pageshow + focus + visibilitychange).
    const fireResume = () => {
      const now = Date.now();
      if (now - lastResumeRef.current < cooldownMs) {
        return;
      }
      lastResumeRef.current = now;
      savedOnResume.current();
    };

    const handlePageShow = (event) => {
      // Only treat bfcache restores as a resume. Plain page loads
      // (persisted === false) must be a no-op so we don't refetch on every
      // normal navigation.
      if (event.persisted === true) {
        fireResume();
      }
    };

    const handleFocus = () => {
      fireResume();
    };

    const handleOnline = () => {
      // The network may not be routable the instant `online` fires; defer the
      // resume briefly so the refetch has a live connection.
      if (onlineTimeoutId) {
        clearTimeout(onlineTimeoutId);
      }
      onlineTimeoutId = setTimeout(() => {
        onlineTimeoutId = null;
        fireResume();
      }, 1500);
    };

    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleOnline);

    // Single consolidated cleanup: remove all listeners and clear any pending
    // online timeout on unmount / when enabled flips false.
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleOnline);
      if (onlineTimeoutId) {
        clearTimeout(onlineTimeoutId);
        onlineTimeoutId = null;
      }
    };
  }, [enabled, cooldownMs]);
}

export default usePageLifecycleEvents;
